import { Button, Flex, Text } from '@billy/design-system'
import { useCallback } from 'react'

import { VatDeclarationsReportField } from '@features/vatDeclarations/types/vatDeclarationsReportField'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useCopyAndNotify } from '../../../../../../../../../hooks/useCopyAndNotify'
import { formatAmountWithCurrency } from '../../../../../../../utils/formatAmountWithCurrency'
import { BodyCell } from './styles'

interface TableBodyProps {
  tableRowsData: VatDeclarationsReportField[]
}

export const TableBody = ({ tableRowsData }: TableBodyProps) => {
  const { organization } = useUserOrganization()
  const { copy } = useCopyAndNotify()
  const currency = organization?.baseCurrencyId
  const locale = organization?.localeId

  const handleCopyButtonClick = useCallback((amount: number) => copy(String(amount)), [copy])

  const renderContent = (text: string, amount: number) => (
    <Flex alignItems="center" justifyContent="flex-end">
      <Text>{text}</Text>
      <Button icon="copy" onClick={() => handleCopyButtonClick(amount)} variant="text" size="sm" />
    </Flex>
  )

  return (
    <>
      {tableRowsData.map(({ name, symbol, turnoverAmount, vatAmount }: VatDeclarationsReportField) => {
        const shouldShowTurnoverAmount = turnoverAmount !== undefined
        const shouldShowVatAmount = vatAmount !== undefined
        const hasBothAmounts = shouldShowTurnoverAmount && shouldShowVatAmount

        const amountWithCurrency = vatAmount
          ? formatAmountWithCurrency({
              amount: vatAmount,
              locale,
              currency,
            })
          : '0'

        const turnoverAmountWithCurrency = turnoverAmount
          ? formatAmountWithCurrency({
              amount: turnoverAmount,
              locale,
              currency,
            })
          : '0'

        const getFourthColumnContent = () => {
          /* If both amounts are present, 3rd column is already filled turnover, 4th should be VAT  */
          if (hasBothAmounts) {
            return renderContent(amountWithCurrency, vatAmount)
          }

          /* If only turnover is there, show turnover, as it is not filled in the 3rd column without VAT */
          if (shouldShowTurnoverAmount) {
            return renderContent(turnoverAmountWithCurrency, turnoverAmount)
          }

          /* No turnover - only show VAT */
          return shouldShowVatAmount && renderContent(amountWithCurrency, vatAmount)
        }

        return (
          <tr key={`${name}-${symbol}`}>
            <BodyCell>
              <Text>{symbol}</Text>
            </BodyCell>
            <BodyCell>
              <Text>{name}</Text>
            </BodyCell>
            <BodyCell>{hasBothAmounts && renderContent(turnoverAmountWithCurrency, turnoverAmount)}</BodyCell>
            <BodyCell>{getFourthColumnContent()}</BodyCell>
          </tr>
        )
      })}
    </>
  )
}
